import { ReactComponent as QuoteExpiredIcon } from '@basqet-dashboard/common/assets/icons/quote-expired.svg';
import { ReactComponent as SwapIcon } from '@basqet-dashboard/common/assets/icons/swap-arrows.svg';
import { Button } from '@basqet-dashboard/common/components';
import { formatCryptoCurrency } from '@basqet-dashboard/common/utils';
import PropTypes from 'prop-types';
import React from 'react';

import { QuoteCountdownRing } from './quote-countdown-ring';
import { WalletSelect } from './wallet-select';

const PERCENT_PRESETS = [0.1, 0.25, 0.5, 0.75, 1];

// Fiat currencies that aren't convertible here — hidden from both selects.
const EXCLUDED_CONVERSION_SLUGS = new Set(['GBP', 'USD', 'EUR']);

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatBalance = (value, slug) =>
  `${formatCryptoCurrency(safeNumber(value))} ${String(slug || '').toUpperCase()}`;

const buildWalletOptions = (wallets) =>
  wallets
    .filter(
      (wallet) =>
        wallet?.currency?.slug &&
        !EXCLUDED_CONVERSION_SLUGS.has(String(wallet.currency.slug).toUpperCase())
    )
    .map((wallet) => ({
      value: wallet.currency.slug,
      label: wallet.currency.slug,
      name: wallet.currency.name,
      balance: safeNumber(wallet.available_balance),
      iconUrl: wallet.currency.icon_url
    }))
    .sort((a, b) => {
      const aFunded = a.balance > 0;
      const bFunded = b.balance > 0;
      if (aFunded !== bFunded) return aFunded ? -1 : 1;
      return (a.name || a.value).localeCompare(b.name || b.value, undefined, {
        sensitivity: 'base'
      });
    });

const EnterAmountStep = ({
  wallets,
  fromSlug,
  toSlug,
  setFromSlug,
  setToSlug,
  amountInput,
  setAmountInput,
  fromWallet,
  onPercent,
  onSwap,
  isFetchingQuote,
  quote,
  quoteExpired,
  remainingSeconds,
  amountError,
  onAmountBlur,
  onRequote,
  onNext,
  nextEnabled
}) => {
  const walletOptions = buildWalletOptions(wallets);

  const exchangeRate = quote ? safeNumber(quote.exchange_rate) : 0;
  const toAmount = quote ? safeNumber(quote.to_amount) : 0;

  return (
    <div className="convert-step">
      <div className="convert-step__group">
        <span className="convert-step__label">From</span>
        <div className={`convert-step__field${amountError ? ' convert-step__field--error' : ''}`}>
          <WalletSelect
            value={fromSlug}
            options={walletOptions.filter((option) => option.value !== toSlug)}
            placeholder="Select source wallet"
            onChange={setFromSlug}
          />
          {fromSlug && (
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="convert-step__amount"
              aria-label="Amount to convert"
              aria-invalid={Boolean(amountError)}
              placeholder="0"
              value={amountInput}
              onChange={(event) => setAmountInput(event.target.value)}
              onBlur={onAmountBlur}
            />
          )}
        </div>

        {fromSlug && (
          <div className="convert-step__balance-row">
            <span className="convert-step__balance">
              Available Bal: {formatBalance(fromWallet?.available_balance, fromSlug)}
            </span>
            {amountError && (
              <span className="convert-step__error" role="alert">
                <span aria-hidden>ⓘ</span> {amountError}
              </span>
            )}
          </div>
        )}

        {fromSlug && (
          <div className="convert-step__presets">
            {PERCENT_PRESETS.map((fraction) => (
              <button
                type="button"
                key={fraction}
                className="convert-step__preset"
                onClick={() => onPercent(fraction)}>
                {fraction === 1 ? 'Max' : `${fraction * 100}%`}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className="convert-step__swap"
        aria-label="Swap source and destination"
        onClick={onSwap}
        disabled={!fromSlug || !toSlug}>
        <SwapIcon />
      </button>

      <div className="convert-step__group">
        <span className="convert-step__label">To</span>
        <div className="convert-step__field">
          <WalletSelect
            value={toSlug}
            options={walletOptions.filter((option) => option.value !== fromSlug)}
            placeholder="Select destination wallet"
            onChange={setToSlug}
          />
          <div className="convert-step__amount convert-step__amount--readonly" aria-live="polite">
            {isFetchingQuote && !quote && <span aria-hidden className="convert-step__spinner" />}
            {quote && toAmount.toLocaleString('en-US', { maximumFractionDigits: 8 })}
          </div>
        </div>

        {toSlug && (
          <div className="convert-step__quote-status">
            {isFetchingQuote && !quote && (
              <span className="convert-step__muted">Fetching market price, please wait…</span>
            )}
            {quote && !quoteExpired && (
              <>
                <span className="convert-step__rate">Exchange rate</span>
                <span className="convert-step__rate-value">
                  1 {quote.from_currency} ={' '}
                  {exchangeRate.toLocaleString('en-US', { maximumFractionDigits: 8 })}{' '}
                  {quote.to_currency}
                </span>
              </>
            )}
          </div>
        )}

        {quote && !quoteExpired && remainingSeconds > 0 && (
          <div className="convert-step__countdown" role="status">
            <QuoteCountdownRing
              remainingSeconds={remainingSeconds}
              className="convert-step__countdown-icon"
            />
            <span>
              This quote expires in <strong>{remainingSeconds} seconds</strong>
            </span>
          </div>
        )}

        {quote && quoteExpired && (
          <div className="convert-step__expired" role="alert">
            <QuoteExpiredIcon aria-hidden className="convert-step__expired-icon" />
            <span>This quote has expired. Please generate a new quote to continue.</span>
            <Button onClick={onRequote} disabled={isFetchingQuote}>
              {isFetchingQuote ? 'Loading…' : 'Requote'}
            </Button>
          </div>
        )}
      </div>

      <Button isFull onClick={onNext} disabled={!nextEnabled} className="convert-step__next">
        Next
      </Button>
    </div>
  );
};

EnterAmountStep.propTypes = {
  wallets: PropTypes.array.isRequired,
  fromSlug: PropTypes.string.isRequired,
  toSlug: PropTypes.string.isRequired,
  setFromSlug: PropTypes.func.isRequired,
  setToSlug: PropTypes.func.isRequired,
  amountInput: PropTypes.string.isRequired,
  setAmountInput: PropTypes.func.isRequired,
  fromWallet: PropTypes.object,
  onPercent: PropTypes.func.isRequired,
  onSwap: PropTypes.func.isRequired,
  isFetchingQuote: PropTypes.bool,
  quote: PropTypes.object,
  quoteExpired: PropTypes.bool,
  remainingSeconds: PropTypes.number,
  amountError: PropTypes.string,
  onAmountBlur: PropTypes.func,
  onRequote: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  nextEnabled: PropTypes.bool
};

EnterAmountStep.defaultProps = {
  fromWallet: null,
  isFetchingQuote: false,
  quote: null,
  quoteExpired: false,
  remainingSeconds: 0,
  amountError: '',
  onAmountBlur: () => {},
  nextEnabled: false
};

export { buildWalletOptions, EnterAmountStep };
